import {
  FileType,
  GameAssets,
  GameCharacter,
  GameImage,
  GameSound,
  I18n,
  Locale,
  Transcriptions,
  TtsLocale,
  UserId,
  Uuid,
} from '../../models/Base';
import { RepositoryElement } from '../../models/Repository';
import { Range } from '../../models/Utils';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';

export enum GameType {
  TextOrdering = 'textOrdering',
  FingerWriting = 'fingerWriting',
  // MultipleChoice = 'multipleChoice',
  // Pairing = 'pairing',
  // Speaking = 'speaking',
  // ManuallyEvaluatedSpeaking = 'speaking+manualEvaluation',
  // Conversation = 'conversation',
  // ManuallyEvaluatedConversation = 'conversation+manualEvaluation',
  // Ordering = 'ordering',
  // Matching = 'matching',

  // below not exist in server
  // Abacus = 'abacus',
  // Rhythm = 'rhythm',
  // MediaPlayer = 'mediaPlayer',
}

export enum GameScenario {
  Default = 'default',
  Bridge = 'bridge',
  Press = 'press',
}

export interface GameInstruction {
  text: string;
  images?: GameImage[];
  sound?: GameSound;
}

export enum GameChoiceType {
  Text = 'text',
  Image = 'image',
  Resource = 'resource',
}

interface TextChoice {
  type: GameChoiceType.Text;
  text: string;
  locale: TtsLocale;
}

export type GameChoice =
  | TextChoice
  | GameImage<GameChoiceType.Image | GameChoiceType.Resource>;

// textOrdering
export enum TextOrderingUnitType {
  Blank = 'blank',
  Hint = 'hint',
  Candidate = 'candidate',
}

export interface TextOrderingUnit {
  key: string;
  type: TextOrderingUnitType;
  distractors?: 'none' | 'auto' | string[];
  ttsAudio?: Uuid;
}

export interface TextOrderingWord {
  word: string;
  units: TextOrderingUnit[];
  ttsAudio?: Uuid;
}

export enum TextOrderingSoundMode {
  Normal = 'normal',
  Dictation = 'dictation',
  Silent = 'silent',
}

export interface TextOrderingAnswer {
  words: TextOrderingWord[];
  locale: TtsLocale;
  soundMode: TextOrderingSoundMode;
  answerFirstLetter: boolean;
  isLyrics?: boolean;
  useColor?: boolean;
}

export interface TextOrderingVariant {
  instruction: GameInstruction;
  answer: TextOrderingAnswer;
}

// fingerWriting
export interface FingerWritingWord {
  word: string;
  characters: GameCharacter<false>[];
  ttsAudio?: Uuid;
}

export enum FingerWritingSoundMode {
  SpeakBeforehand = 'speakBeforehand',
  SpeakAfterwards = 'speakAfterwards',
  Silent = 'silent',
}

export interface StrokeSettings {
  useComponent?: boolean;
  useHint?: boolean;
  animationSpeed?: number; // minimum = 0.0
  minAnimationDistance?: number; // minimum = 0.0
  animationDelay?: number; // minimum = 0.0
  highlightDuration?: number; // minimum = 0.0
  distanceTolerance?: number; // range = [-10.0, 10.0]
  lengthTolerance?: number; // range = [-10.0, 10.0]
  directionTolerance?: number; // range = [-10.0, 10.0]
  extraChances?: number; // minimum = 0
}

export interface FingerWritingAnswer {
  words: FingerWritingWord[];
  locale: TtsLocale;
  soundMode: FingerWritingSoundMode;
  transcriptions?: Transcriptions;
  strokeSettings?: StrokeSettings;
}

export enum FingerWritingMode {
  Writing = 'writing', // normal writing game (Handwriting - Write to Answer)
  Matching = 'matching', // play animation to choose correct word (Ink-less Handwriting)
  Ordering = 'ordering', // choose the stroke 1 by 1 to form word (Stroke by spelling)
}

export interface FingerWritingVariant {
  instruction: GameInstruction;
  answer: FingerWritingAnswer;
  mode: FingerWritingMode;
}

// multipleChoice
export interface MultipleChoiceVariant {
  instruction: GameInstruction;
  context?: GameInstruction;
  key: GameChoice;
  distractors: GameChoice[];
}

// pairing
// TODO: RequireAtLeastOneOf
interface PairingHint {
  text?: string;
  sound?: GameSound;
}

export interface PairingPair {
  hint: PairingHint;
  answer: GameChoice;

  // local use only
  repeat?: number;
  enablePractice?: boolean;
}

export interface PairingVariant {
  pairs: PairingPair[];
}

// speaking
export enum SpeakingMode {
  Word = 'word',
  Character = 'character',
}

interface SpeakingGameAnswer {
  text: string;
  locale: TtsLocale;
  mode: SpeakingMode;
  sound?: GameSound;
}

export interface SpeakingVariant {
  instruction: GameInstruction;
  answer: SpeakingGameAnswer;
}

// conversation
export enum ConversationMode {
  FollowMeToSpeak = 'followMeToSpeak',
  ReadAloud = 'readAloud',
  Recite = 'recite',
  Converse = 'converse',
}

export interface ConversationComponent {
  text?: string;
  image?: GameImage;
  sound?: GameSound;
  video?: Uuid;
}

export interface Dialogue {
  instruction: ConversationComponent;
  answer: ConversationComponent;
}

export interface GameReply {
  key: Range<0, 6> | string;
  sound: GameSound;
}

export interface RecordingSettings {
  shouldAutoStart: boolean;
  timeLimit: number;
}

export interface ConversationVariant {
  mode:
    | ConversationMode.FollowMeToSpeak
    | ConversationMode.ReadAloud
    | ConversationMode.Recite;
  dialogues: Dialogue[]; // TODO: customize component for mode?
  ending: ConversationComponent;
  replies: GameReply[];
  recordingSettings?: RecordingSettings;
}

export interface ManuallyEvaluatedConversationVariant {
  mode: ConversationMode.Converse;
  dialogues: Dialogue[]; // TODO: customize component for mode?
  ending: ConversationComponent;
  replies: GameReply[];
  recordingSettings?: RecordingSettings;
}

// ordering
export interface OrderingUnit {
  key: string;
  ttsAudio?: Uuid;
}

export interface OrderingAnswer {
  units: OrderingUnit[];
  locale: TtsLocale;
}

export interface OrderingVariant {
  instruction: GameInstruction;
  answer: OrderingAnswer;
}

// matching
// TODO: RequireAtLeastOneOf
export interface MatchingChoice {
  text?: string;
  image?: GameImage;
  sound?: GameSound;
}

interface MatchingPair {
  first: MatchingChoice;
  second: MatchingChoice;
}

export interface MatchingVariant {
  instruction: GameInstruction;
  pairs: MatchingPair[];
}

//
export type GameTypes = {
  [GameType.TextOrdering]: {
    scenario: GameScenario;
    variant: TextOrderingVariant;
  };
  [GameType.FingerWriting]: {
    scenario: GameScenario.Default;
    variant: FingerWritingVariant;
  };
  // [GameType.MultipleChoice]: {
  //   scenario: GameScenario.Default;
  //   variant: MultipleChoiceVariant;
  // };
  // [GameType.Pairing]: {
  //   scenario: GameScenario.Default;
  //   variant: PairingVariant;
  // };
  // [GameType.Speaking]: {
  //   scenario: GameScenario.Default;
  //   variant: SpeakingVariant;
  // };
  // [GameType.ManuallyEvaluatedSpeaking]: {
  //   scenario: GameScenario.Default;
  //   variant: SpeakingVariant;
  // };
  // [GameType.Conversation]: {
  //   scenario: GameScenario.Default;
  //   variant: ConversationVariant;
  // };
  // [GameType.ManuallyEvaluatedConversation]: {
  //   scenario: GameScenario.Default;
  //   variant: ManuallyEvaluatedConversationVariant;
  // };
  // [GameType.Ordering]: {
  //   scenario: GameScenario.Default;
  //   variant: OrderingVariant;
  // };
  // [GameType.Matching]: {
  //   scenario: GameScenario.Default;
  //   variant: MatchingVariant;
  // };
};

export type GameVariant = TextOrderingVariant | FingerWritingVariant;
// | MultipleChoiceVariant
// | PairingVariant
// | SpeakingVariant
// | ConversationVariant
// | ManuallyEvaluatedConversationVariant
// | OrderingVariant
// | MatchingVariant;

export type GameData<V extends GameVariant = GameVariant> = {
  translatedBy?: 'google';
  title: string;
  description: string;
  variants: V[];
};
export type GameDataWithVariant<V extends GameVariant = GameVariant> = Omit<
  GameData<V>,
  'variants'
> &
  V;
export type I18nGameData<V extends GameVariant = GameVariant> = I18n<
  GameData<V>
>;

export type ConcreteGameJson<G extends GameType, T = number> = {
  readonly gameId: Uuid; // identifier
  gameCollectionId: Uuid;
  classroomId: Uuid;
  createdBy: UserId;
  createdAt: T;
  lastModifiedAt: T;
  isSuspended?: boolean;
  gameType: G;
  version: string;
  i18nGameData: I18nGameData<GameTypes[G]['variant']>;
  scenario: GameTypes[G]['scenario'];
  timeLimit: 'auto' | number;
  // background: ImageOrSet; // TODO
  dependencies: GameAssets;
};

export type GameJson<T = number | Date> = {
  [G in GameType]: ConcreteGameJson<G, T>;
}[GameType];

export class ConcreteGame<G extends GameType>
  extends RepositoryElement
  implements ConcreteGameJson<G, Date>
{
  readonly gameId!: Uuid;
  gameCollectionId!: Uuid;
  classroomId!: Uuid;
  createdBy!: UserId;
  createdAt!: Date;
  lastModifiedAt!: Date;
  isSuspended?: boolean;
  gameType!: G;
  version!: string;
  i18nGameData!: I18nGameData<GameTypes[G]['variant']>;
  scenario!: GameTypes[G]['scenario'];
  timeLimit!: 'auto' | number;
  // background!: ImageOrSet; // TODO
  dependencies!: GameAssets;

  constructor(game: ConcreteGameJson<G, Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(game));
  }

  id(): string {
    return this.gameId;
  }

  toJSON(): ConcreteGameJson<G> {
    return deepTransformDate(this) as unknown as ConcreteGameJson<G>;
  }

  getMedias<F extends Exclude<FileType, FileType.Icon>>(
    ..._types: F[]
  ): { fileType: F; fileId: Uuid }[] {
    type Return = { fileType: F; fileId: Uuid };
    return _types.flatMap<Return>((type) => {
      switch (type) {
        case FileType.Image:
          return this.dependencies.images.map((fileId) => ({
            fileId,
            fileType: FileType.Image,
          })) as Return[];
        case FileType.Audio:
          return this.dependencies.audios.map((fileId) => ({
            fileId,
            fileType: FileType.Audio,
          })) as Return[];
        case FileType.Video:
          return this.dependencies.videos.map((fileId) => ({
            fileId,
            fileType: FileType.Video,
          })) as Return[];
        case FileType.ResourceImage:
          return this.dependencies.resourceImages.map((fileId) => ({
            fileId,
            fileType: FileType.ResourceImage,
          })) as Return[];
        case FileType.Stroke:
          return this.dependencies.strokes.map((fileId) => ({
            fileId,
            fileType: FileType.Stroke,
          })) as Return[];
        case FileType.TtsAudio:
          return this.dependencies.ttsAudios.map((fileId) => ({
            fileId,
            fileType: FileType.TtsAudio,
          })) as Return[];
        default:
          return [] as Return[];
      }
    });
  }

  get previewImage():
    | { fileType: FileType.Image | FileType.ResourceImage; fileId: Uuid }
    | undefined {
    const image = this.getMedias(FileType.Image)[0];
    if (image) return image;
    const resourceImage = this.getMedias(FileType.ResourceImage)[0];
    if (resourceImage) return resourceImage;
    return undefined;
  }

  get imageId(): string | undefined {
    return this.dependencies.images[0] ?? this.dependencies.resourceImages[0];
  }
}

export type Game = { [G in GameType]: ConcreteGame<G> }[GameType];

export const jsonToGame = (game: GameJson): Game => {
  switch (game.gameType) {
    case GameType.TextOrdering:
      return new ConcreteGame<GameType.TextOrdering>(game);
    case GameType.FingerWriting:
      return new ConcreteGame<GameType.FingerWriting>(game);
    // case GameType.MultipleChoice:
    //   return new ConcreteGame<GameType.MultipleChoice>(game);
    // case GameType.Conversation:
    //   return new ConcreteGame<GameType.Conversation>(game);
    // case GameType.ManuallyEvaluatedConversation:
    //   return new ConcreteGame<GameType.ManuallyEvaluatedConversation>(game);
    // case GameType.ManuallyEvaluatedSpeaking:
    //   return new ConcreteGame<GameType.ManuallyEvaluatedSpeaking>(game);
    // case GameType.Matching:
    //   return new ConcreteGame<GameType.Matching>(game);
    // case GameType.Ordering:
    //   return new ConcreteGame<GameType.Ordering>(game);
    // case GameType.Pairing:
    //   return new ConcreteGame<GameType.Pairing>(game);
    // case GameType.Speaking:
    //   return new ConcreteGame<GameType.Speaking>(game);
    default:
      // some old game type
      return new ConcreteGame<never>(game);
  }
};

export const GameExample: Game = new ConcreteGame<GameType.TextOrdering>({
  gameId: 'gameId',
  gameCollectionId: 'gameCollectionId',
  classroomId: 'classroomId',
  createdBy: 'user',
  createdAt: new Date(),
  lastModifiedAt: new Date(),
  gameType: GameType.TextOrdering,
  version: '1.0',
  i18nGameData: {
    resources: {
      'en-US': {
        title: 'Game name',
        description: 'description',
        variants: [
          {
            instruction: {
              text: 'Listen and try to spell words.',
            },
            answer: {
              words: [
                {
                  word: 'abc',
                  units: [
                    {
                      type: TextOrderingUnitType.Blank,
                      key: 'abc',
                    },
                  ],
                },
              ],
              locale: Locale.US,
              soundMode: TextOrderingSoundMode.Normal,
              answerFirstLetter: false,
              isLyrics: false,
              useColor: false,
            },
          },
        ],
      },
    },
    defaultLocale: Locale.US,
  },
  scenario: GameScenario.Default,
  timeLimit: 'auto',
  dependencies: {
    images: [],
    audios: [],
    videos: [],
    resourceImages: [],
    strokes: [],
    ttsAudios: [],
  },
});
